import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenJHReport from "./HiddenJHReport";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── Band → colour map (normalized 0–10 scale) ─────────────────────────────────
const bandColor = (band) => {
  if (!band) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  switch (band.toLowerCase()) {
    case "high":    return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // green
    case "average": return { bg: "#dbeafe", color: "#3f7cac", border: "#bfdbfe" }; // blue
    case "low":     return { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" }; // amber
    default:        return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  }
};

// ── Profile → badge colour map ────────────────────────────────────────────────
const profileColor = (profile) => {
  switch ((profile || "").toLowerCase()) {
    case "arena":     return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // green
    case "driver":    return { bg: "#ede9fe", color: "#6d28d9", border: "#ddd6fe" }; // purple
    case "advisor":   return { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" }; // blue
    case "guarded":   return { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" }; // amber
    default:          return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  }
};

// ── JH dimensions ─────────────────────────────────────────────────────────────
const DIMENSIONS = [
  { key: "exposure", label: "Exposure" },
  { key: "feedback", label: "Feedback" },
];

// ── Profile descriptions (tooltip) ───────────────────────────────────────────
const PROFILE_DESC = {
  arena:   "High Exposure · High Feedback",
  driver:  "High Exposure · Low/Avg Feedback",
  advisor: "Low/Avg Exposure · High Feedback",
  guarded: "Low Exposure · Low Feedback",
};

// ── Profile display labels (data still stores the legacy keys) ────────────────
const PROFILE_LABEL = {
  arena:   "Arena",
  driver:  "Blind",
  advisor: "Concealed",
  guarded: "Discover",
};
const profileLabel = (profile) => PROFILE_LABEL[(profile || "").toLowerCase()] || profile;

export default function GlobalJHResponses({ surveyId }) {
  const [jhData,         setJhData]         = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── Filters ──
  const [filterName,       setFilterName]       = useState("");
  const [filterEmail,      setFilterEmail]      = useState("");
  const [filterProfile,    setFilterProfile]    = useState("");
  const [filterCompany,    setFilterCompany]    = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStartDate,  setFilterStartDate]  = useState("");
  const [filterEndDate,    setFilterEndDate]    = useState("");

  // ── PDF state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchJHResults();
  }, [surveyId]);

  const fetchJHResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/jh/all`,
        { params: { survey_id: surveyId } }
      );
      setJhData(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch JH results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  const uniqueProfiles    = [...new Set(jhData.map(u => u.scores?.profile).filter(Boolean))].sort();
  const uniqueCompanies   = [...new Set(jhData.map(u => u.company).filter(Boolean))].sort();
  const uniqueDepartments = [...new Set(jhData.map(u => u.department).filter(Boolean))].sort();

  const filteredData = jhData.filter((user) => {
    const name       = (user?.name       || "").toLowerCase();
    const email      = (user?.email      || "").toLowerCase();
    const profile    = (user?.scores?.profile || "").toLowerCase();
    const company    = (user?.company    || "").toLowerCase();
    const department = (user?.department || "").toLowerCase();

    const matchName       = name.includes(filterName.toLowerCase());
    const matchEmail      = email.includes(filterEmail.toLowerCase());
    const matchProfile    = filterProfile    === "" || profile    === filterProfile.toLowerCase();
    const matchCompany    = filterCompany    === "" || company    === filterCompany.toLowerCase();
    const matchDepartment = filterDepartment === "" || department === filterDepartment.toLowerCase();

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time);
      submissionDate.setHours(0, 0, 0, 0);

      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchProfile && matchCompany && matchDepartment && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterProfile("");
    setFilterCompany("");
    setFilterDepartment("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── Summary counts ──
  const profileCounts = jhData.reduce((acc, u) => {
    const p = (u.scores?.profile || "Unknown").toLowerCase();
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    const sc = user.scores || {};
    const exposureNum = sc.exposure?.normalized ?? 0;
    const feedbackNum = sc.feedback?.normalized ?? 0;

    setDownloadData({
      userInfo: {
        name:  user?.name  || "Participant",
        email: user?.email || "",
        phone: user?.phone || "",
      },
      scores: {
        exposure: exposureNum,
        feedback: feedbackNum,
      },
      activeProfile: (sc.profile || "").toLowerCase(),
      insightText: "",
      submission_time: user.submission_time,
    });
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading) return <div className="loading-state">Loading Johari Window data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>
       <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer */}
      <HiddenJHReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h3 style={{ margin: 0 }}>🪟 Johari Window Responses ({filteredData.length})</h3>
        <span style={{
          background: "#A80D2C", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Profile summary pills ── */}
      {jhData.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: "8px",
          marginBottom: "16px",
        }}>
          {Object.entries(profileCounts).map(([profile, count]) => {
            const pc = profileColor(profile);
            return (
              <span
                key={profile}
                onClick={() => setFilterProfile(filterProfile === profile ? "" : profile)}
                title="Click to filter by this profile"
                style={{
                  background:   pc.bg,
                  color:        pc.color,
                  border:       `1px solid ${pc.border}`,
                  padding:      "4px 12px",
                  borderRadius: "999px",
                  fontSize:     "12px",
                  fontWeight:   "700",
                  cursor:       "pointer",
                  outline:      filterProfile === profile ? `2px solid ${pc.color}` : "none",
                  outlineOffset: "2px",
                }}
              >
                {profileLabel(profile)}: {count}
              </span>
            );
          })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "140px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Profile</label>
          <select
            value={filterProfile}
            onChange={(e) => setFilterProfile(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Profiles</option>
            {uniqueProfiles.map(p => (
              <option key={p} value={p.toLowerCase()}>{profileLabel(p)}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "140px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "140px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepartments.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {jhData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No Johari Window responses found.</div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No responses match your current filters.</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "220px" }}>
                  Exposure &amp; Feedback Scores
                </th>
                <th style={{ padding: "12px", minWidth: "100px", background: "#fdf6e3" }}>
                  🪟 Profile
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const sc           = user.scores || {};
                const profile      = sc.profile  || "";
                const isGenerating = isGeneratingRow === index;
                const pc           = profileColor(profile);

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time
                        ? <>
                            {new Date(user.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user?.email}<br />
                        {user?.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user?.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* ── Dimension score pills ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {DIMENSIONS.map(({ key, label }) => {
                          const dim   = sc[key] || {};
                          const norm  = dim.normalized ?? null;
                          const band  = dim.band       ?? null;
                          const raw   = dim.raw        ?? null;
                          const c     = bandColor(band);
                          return (
                            <span
                              key={key}
                              title={`Raw: ${raw ?? "—"}  Normalized: ${norm ?? "—"}/10  Band: ${band ?? "—"}`}
                              style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `1px solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   "700",
                                whiteSpace:   "nowrap",
                              }}
                            >
                              {label}: {norm ?? "—"}/10
                              {band && (
                                <span style={{ fontWeight: "500", marginLeft: "4px", opacity: 0.8 }}>
                                  ({band})
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* ── Profile badge ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      {profile ? (
                        <span
                          title={PROFILE_DESC[(profile).toLowerCase()] || ""}
                          style={{
                            background:   pc.bg,
                            color:        pc.color,
                            border:       `1px solid ${pc.border}`,
                            padding:      "4px 10px",
                            borderRadius: "999px",
                            fontSize:     "12px",
                            fontWeight:   "800",
                            whiteSpace:   "nowrap",
                          }}
                        >
                          {profileLabel(profile)}
                        </span>
                      ) : (
                        <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      )}
                    </td>

                    {/* ── Download ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}